import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Upload, FileStack, FolderTree, LogOut, ScanLine, Mail, Zap, Sparkles, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

const primary = [
  { to: "/app", icon: LayoutDashboard, label: "Panoramica", end: true, testid: "nav-dashboard" },
  { to: "/app/chiedi", icon: Sparkles, label: "Chiedi AI", testid: "nav-assistant" },
  { to: "/app/upload", icon: Upload, label: "Carica", testid: "nav-upload" },
  { to: "/app/documenti", icon: FileStack, label: "Documenti", testid: "nav-documents" },
  { to: "/app/cartelle", icon: FolderTree, label: "Cartelle", testid: "nav-folders" },
];

const secondary = [
  { to: "/app/regole", icon: Zap, label: "Regole", testid: "nav-rules" },
  { to: "/app/email", icon: Mail, label: "Import Email", testid: "nav-email" },
];

const desktopItems = [...primary, ...secondary];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const doLogout = () => { logout(); nav("/"); };

  const SidebarContent = (
    <>
      <div className="h-16 border-b border-black/10 flex items-center px-5 gap-2 shrink-0">
        <div className="w-8 h-8 bg-[#0A0A0A] flex items-center justify-center rounded-xl">
          <ScanLine className="w-4 h-4 text-white" />
        </div>
        <span className="font-display font-bold tracking-tighter">Archivio Smart</span>
        <button
          data-testid="drawer-close-btn"
          className="ml-auto p-2 md:hidden hover:bg-black/5 rounded-xl"
          onClick={() => setDrawerOpen(false)}
          aria-label="Chiudi menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <div className="overline text-[#8A8A8A] px-3 py-3">— MENU</div>
        {desktopItems.map(i => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.end}
            data-testid={i.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive ? "bg-[#0A0A0A] text-white" : "text-[#0A0A0A] hover:bg-black/5"
              }`
            }
          >
            <i.icon className="w-4 h-4" strokeWidth={1.75} />
            {i.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-black/10 shrink-0" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="px-3 py-2 mb-2">
          <div className="overline text-[#8A8A8A]">ACCOUNT</div>
          <div className="text-sm font-medium truncate mt-1" data-testid="user-name">{user?.name}</div>
          <div className="text-xs text-[#8A8A8A] truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#0A0A0A] hover:bg-black/5 transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.75} />
          Esci
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F8F8F6] md:flex">
      {/* Mobile top bar */}
      <header
        className="md:hidden sticky top-0 z-30 bg-[#F0F0ED] border-b border-black/10 flex items-center px-4 h-14"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setDrawerOpen(true)}
          className="p-2 -ml-2 hover:bg-black/5 rounded-xl"
          aria-label="Apri menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 mx-auto pr-6">
          <div className="w-7 h-7 bg-[#0A0A0A] flex items-center justify-center rounded-xl">
            <ScanLine className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="font-display font-bold tracking-tighter text-sm">Archivio Smart</span>
        </div>
      </header>

      {/* Desktop sidebar (>=md) */}
      <aside className="hidden md:flex w-60 bg-[#F0F0ED] border-r border-black/10 flex-col shrink-0 h-screen sticky top-0">
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="relative bg-[#F0F0ED] w-72 max-w-[85vw] flex flex-col shadow-xl"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            {SidebarContent}
          </aside>
        </div>
      )}

      {/* Main content — pad bottom on mobile so bottom nav doesn't overlap */}
      <main className="flex-1 min-w-0 overflow-x-hidden pb-24 md:pb-0">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-lg border-t border-black/10"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="grid grid-cols-5">
          {primary.map(i => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.end}
              data-testid={`mobile-${i.testid}`}
              className={({ isActive }) =>
                `relative flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors ${
                  isActive ? "text-[#0A0A0A]" : "text-[#8A8A8A]"
                }`
              }
            >
              <i.icon className="w-5 h-5" strokeWidth={1.75} />
              <span className="truncate max-w-full px-1">{i.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
